import React, { useState } from "react";
import StaticPage from "./StaticPage";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Mail, Phone, MapPin, Send, CheckCircle2 } from "lucide-react";
import { COMPANY } from "@/lib/siteConfig";

const EMPTY = { name: "", email: "", school: "", mobile: "", message: "" };

export default function Contact() {
  const [f, setF] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    // Simple guardrails (backend validates too)
    if (f.name.trim().length < 2) return toast.error("Please enter your name");
    if (!/^\S+@\S+\.\S+$/.test(f.email)) return toast.error("Please enter a valid email");
    if (f.message.trim().length < 5) return toast.error("Please add a short message");
    setSubmitting(true);
    try {
      await api.post("/contact", {
        name: f.name.trim(),
        email: f.email.trim(),
        school: f.school.trim(),
        mobile: f.mobile.trim(),
        message: f.message.trim(),
      });
      setSent(true);
      setF(EMPTY);
      toast.success("Thanks! Our team will reach out within 24 hours.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StaticPage
      title="Get in touch"
      subtitle="Book a demo, ask a question or explore a partnership — we usually reply within 24 hours."
    >
      <div className="not-prose grid lg:grid-cols-5 gap-8 mt-4">
        {/* Left column – contact channels */}
        <aside className="lg:col-span-2 space-y-5">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="text-xs uppercase tracking-widest text-brand-greenDark font-semibold">
              Sales &amp; Demos
            </div>
            <div className="mt-3 flex items-start gap-3">
              <Mail size={18} className="text-brand-blue mt-1" />
              <div>
                <div className="text-sm text-slate-500">Email</div>
                <a href={COMPANY.emailHref} data-testid="contact-email-link"
                   className="font-semibold text-brand-navy hover:text-brand-blue">
                  {COMPANY.email}
                </a>
              </div>
            </div>
            <div className="mt-4 flex items-start gap-3">
              <Phone size={18} className="text-brand-blue mt-1" />
              <div>
                <div className="text-sm text-slate-500">Phone / WhatsApp</div>
                <a href={COMPANY.phoneHref} data-testid="contact-phone-link"
                   className="font-semibold text-brand-navy hover:text-brand-blue">
                  {COMPANY.phone}
                </a>
              </div>
            </div>
            <div className="mt-4 flex items-start gap-3">
              <MapPin size={18} className="text-brand-blue mt-1" />
              <div>
                <div className="text-sm text-slate-500">Head office</div>
                <div className="font-semibold text-brand-navy" data-testid="contact-address">
                  {COMPANY.address.line1}<br />
                  {COMPANY.address.line2}<br />
                  {COMPANY.address.line3}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-brand-blueSoft border border-blue-100 p-6">
            <div className="text-sm font-heading font-semibold text-brand-navy">
              What happens after you write in?
            </div>
            <ul className="mt-3 space-y-2 text-sm text-slate-700">
              {[
                "We reply within 24 hours (usually the same day).",
                "A short 15-minute discovery call to understand your school.",
                "A tailored 20-minute walkthrough on your device.",
                "Pilot proposal — no obligation.",
              ].map((line) => (
                <li key={line} className="flex items-start gap-2">
                  <CheckCircle2 size={16} className="text-brand-green mt-0.5" />
                  {line}
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Right column – form */}
        <div className="lg:col-span-3">
          {sent ? (
            <div
              data-testid="contact-success"
              className="rounded-2xl border border-emerald-200 bg-brand-greenSoft p-8 text-center"
            >
              <div className="w-14 h-14 rounded-full bg-white grid place-items-center mx-auto">
                <CheckCircle2 size={28} className="text-brand-green" />
              </div>
              <h3 className="mt-4 font-heading font-semibold text-xl text-brand-navy">
                Thanks — your message is on its way.
              </h3>
              <p className="mt-2 text-slate-700 text-sm max-w-md mx-auto">
                Our team will reach out within 24 hours. If it's urgent, feel free to
                call us on{" "}
                <a href={COMPANY.phoneHref} className="text-brand-blue font-semibold">
                  {COMPANY.phone}
                </a>.
              </p>
              <button
                data-testid="contact-send-another"
                onClick={() => setSent(false)}
                className="mt-6 text-sm font-semibold text-brand-blue hover:text-brand-blueDark"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 space-y-4"
              data-testid="contact-form"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Full name *</label>
                  <input required data-testid="contact-name" placeholder="Meera Nair"
                    value={f.name} onChange={set("name")}
                    className="mt-1 w-full rounded-md border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-brand-blue" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">School name</label>
                  <input data-testid="contact-school" placeholder="Delhi Public School"
                    value={f.school} onChange={set("school")}
                    className="mt-1 w-full rounded-md border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-brand-blue" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Mobile number</label>
                  <input data-testid="contact-mobile" placeholder="+91 98xxxxxxxx" inputMode="tel"
                    value={f.mobile} onChange={set("mobile")}
                    className="mt-1 w-full rounded-md border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-brand-blue" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Email *</label>
                  <input required type="email" data-testid="contact-email" placeholder="you@school.edu.in"
                    value={f.email} onChange={set("email")}
                    className="mt-1 w-full rounded-md border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-brand-blue" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">How can we help? *</label>
                <textarea required rows={5} data-testid="contact-msg"
                  placeholder="Tell us a little about your school, student strength, and what you'd like to learn more about."
                  value={f.message} onChange={set("message")}
                  className="mt-1 w-full rounded-md border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-brand-blue" />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-md bg-brand-blue text-white font-semibold px-6 py-3 hover:bg-brand-blueDark transition-colors duration-200 disabled:opacity-60"
              >
                {submitting ? "Sending…" : <>Send message <Send size={16} /></>}
              </button>
              <p className="text-xs text-slate-500">
                By submitting, you agree to be contacted by SchoolLane about your enquiry.
                We do not share your details with any third party.
              </p>
            </form>
          )}
        </div>
      </div>
    </StaticPage>
  );
}
